import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  InputAdornment,
  LinearProgress,
  Paper,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Tabs,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import { alpha, styled, useTheme } from '@mui/material/styles';
import {
  AlertTriangle as AlertTriangleIcon,
  Bot as BotIcon,
  CheckCircle2 as CheckCircle2Icon,
  Download as DownloadIcon,
  Eye as EyeIcon,
  RefreshCcw as RefreshIcon,
  Search as SearchIcon,
  Sparkles as SparklesIcon,
  Trash2 as Trash2Icon
} from 'lucide-react';
import { toast } from 'react-toastify';
import api from '../services/api';

const StyledCard = styled(Card)(({ theme }) => ({
  boxShadow: '0 0 2px 0 rgba(145, 158, 171, 0.2), 0 12px 24px -4px rgba(145, 158, 171, 0.12)',
  borderRadius: 16,
  overflow: 'hidden',
  backgroundImage: 'none',
  backgroundColor: theme.palette.background.paper
}));

const SORTABLE_COLUMNS = {
  id: 'id',
  created_at: 'created_at',
  pratica_titolo: 'pratica_titolo',
  template_nome: 'template_nome',
  stato: 'stato',
  modello_ai_snapshot: 'modello_ai_snapshot',
  token_totali: 'token_totali',
  costo_usd: 'costo_usd',
  richiesto_da_nome: 'richiesto_da_nome',
  completata_at: 'completata_at'
};

const STATUS_CHIPS = {
  in_corso: { color: 'info', variant: 'filled', label: 'In corso' },
  completata: { color: 'success', variant: 'filled', label: 'Completata' },
  errore: { color: 'error', variant: 'filled', label: 'Errore' }
};

const ALL_OPTION = { value: '', label: 'Tutti' };

const normalizeDraftContent = (value) => {
  const normalized = String(value || '').replace(/\r\n/g, '\n').trim();
  return normalized || 'Nessun contenuto generato disponibile.';
};

const normalizeInlineText = (value) =>
  String(value || '')
    .replace(/\*\*(.*?)\*\*/g, '$1')
    .replace(/__(.*?)__/g, '$1')
    .replace(/\*(.*?)\*/g, '$1')
    .replace(/_(.*?)_/g, '$1')
    .trim();

const parseDraftBlocks = (value) => {
  const normalized = normalizeDraftContent(value);
  const lines = normalized.split('\n');
  const blocks = [];
  let paragraphBuffer = [];

  const flushParagraph = () => {
    if (!paragraphBuffer.length) {
      return;
    }

    blocks.push({
      type: 'paragraph',
      text: normalizeInlineText(paragraphBuffer.join(' '))
    });
    paragraphBuffer = [];
  };

  for (let index = 0; index < lines.length; index += 1) {
    const rawLine = String(lines[index] || '').replace(/\t/g, ' ').trim();

    if (!rawLine) {
      flushParagraph();
      continue;
    }

    const markdownHeading = rawLine.match(/^(#{1,6})\s+(.+)$/);
    if (markdownHeading) {
      flushParagraph();
      blocks.push({
        type: 'heading',
        level: Math.min(markdownHeading[1].length, 3),
        text: normalizeInlineText(markdownHeading[2])
      });
      continue;
    }

    const bulletItem = rawLine.match(/^[-*•]\s+(.+)$/);
    if (bulletItem) {
      flushParagraph();
      const items = [normalizeInlineText(bulletItem[1])];
      while (index + 1 < lines.length) {
        const nextLine = String(lines[index + 1] || '').trim();
        const nextBullet = nextLine.match(/^[-*•]\s+(.+)$/);
        if (!nextBullet) {
          break;
        }
        items.push(normalizeInlineText(nextBullet[1]));
        index += 1;
      }
      blocks.push({ type: 'ul', items });
      continue;
    }

    const orderedItem = rawLine.match(/^\d+[.)]\s+(.+)$/);
    if (orderedItem) {
      flushParagraph();
      const items = [normalizeInlineText(orderedItem[1])];
      while (index + 1 < lines.length) {
        const nextLine = String(lines[index + 1] || '').trim();
        const nextOrdered = nextLine.match(/^\d+[.)]\s+(.+)$/);
        if (!nextOrdered) {
          break;
        }
        items.push(normalizeInlineText(nextOrdered[1]));
        index += 1;
      }
      blocks.push({ type: 'ol', items });
      continue;
    }

    const upperHeadingCandidate =
      rawLine.length <= 120 &&
      rawLine === rawLine.toUpperCase() &&
      /[A-ZÀ-Ý]/.test(rawLine) &&
      !/[.:;]$/.test(rawLine);

    if (upperHeadingCandidate) {
      flushParagraph();
      blocks.push({
        type: 'heading',
        level: 2,
        text: normalizeInlineText(rawLine)
      });
      continue;
    }

    paragraphBuffer.push(rawLine);
  }

  flushParagraph();
  return blocks;
};

const renderInlineMarkdownNodes = (value) => {
  const text = String(value || '');
  const parts = text.split(/(\*\*.*?\*\*|__.*?__|\*.*?\*|_.*?_)/g).filter(Boolean);

  return parts.map((part, index) => {
    if (
      (part.startsWith('**') && part.endsWith('**')) ||
      (part.startsWith('__') && part.endsWith('__'))
    ) {
      return <strong key={`strong-${index}`}>{part.slice(2, -2)}</strong>;
    }

    if (
      (part.startsWith('*') && part.endsWith('*')) ||
      (part.startsWith('_') && part.endsWith('_'))
    ) {
      return <em key={`em-${index}`}>{part.slice(1, -1)}</em>;
    }

    return <span key={`text-${index}`}>{part}</span>;
  });
};

const DraftContentPreview = ({ value, outputFormat = 'markdown' }) => {
  const theme = useTheme();
  const blocks = useMemo(() => parseDraftBlocks(value), [value]);
  const normalizedFormat = String(outputFormat || '').toLowerCase();

  if (normalizedFormat === 'html') {
    const body = String(value || '').trim();
    return (
      <Paper
        variant="outlined"
        sx={{
          p: 0,
          minHeight: 320,
          overflow: 'hidden',
          bgcolor: theme.palette.mode === 'dark' ? alpha(theme.palette.common.white, 0.04) : theme.palette.background.paper,
          borderColor: theme.palette.divider
        }}
      >
        <iframe
          title="bozza-html"
          sandbox=""
          style={{ width: '100%', height: 540, border: 0, background: 'transparent' }}
          srcDoc={`<!doctype html><html lang="it"><head><meta charset="utf-8" /><style>
            body{font-family:"Times New Roman",Times,serif;line-height:1.55;margin:28px 34px;font-size:12pt;color:${
              theme.palette.mode === 'dark' ? '#e5e7eb' : '#111827'
            };background:${theme.palette.mode === 'dark' ? '#0b1220' : '#ffffff'};}
            p{margin:0 0 10px 0;}
          </style></head><body>${body}</body></html>`}
        />
      </Paper>
    );
  }

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 3,
        minHeight: 320,
        bgcolor: theme.palette.mode === 'dark' ? alpha(theme.palette.common.white, 0.04) : theme.palette.background.paper,
        borderColor: theme.palette.divider,
        '& h1, & h2, & h3': {
          color: theme.palette.text.primary,
          fontWeight: 800,
          lineHeight: 1.3,
          mt: 0,
          mb: 1.25
        },
        '& h1': { fontSize: 24 },
        '& h2': { fontSize: 20, mt: 2.25 },
        '& h3': { fontSize: 17, mt: 2 },
        '& p': {
          fontSize: 14,
          lineHeight: 1.8,
          color: theme.palette.text.primary,
          textAlign: 'justify',
          mt: 0,
          mb: 1.5
        },
        '& ul, & ol': {
          mt: 0,
          mb: 1.75,
          pl: 3,
          color: theme.palette.text.primary
        },
        '& li': {
          fontSize: 14,
          lineHeight: 1.8,
          mb: 0.5,
          color: theme.palette.text.primary
        }
      }}
    >
      {blocks.map((block, index) => {
        if (block.type === 'heading') {
          if (block.level <= 1) {
            return <Typography component="h1" key={`heading-${index}`}>{renderInlineMarkdownNodes(block.text)}</Typography>;
          }
          if (block.level === 2) {
            return <Typography component="h2" key={`heading-${index}`}>{renderInlineMarkdownNodes(block.text)}</Typography>;
          }
          return <Typography component="h3" key={`heading-${index}`}>{renderInlineMarkdownNodes(block.text)}</Typography>;
        }

        if (block.type === 'ul') {
          return (
            <Box component="ul" key={`ul-${index}`}>
              {block.items.map((item, itemIndex) => (
                <li key={`ul-item-${index}-${itemIndex}`}>{renderInlineMarkdownNodes(item)}</li>
              ))}
            </Box>
          );
        }

        if (block.type === 'ol') {
          return (
            <Box component="ol" key={`ol-${index}`}>
              {block.items.map((item, itemIndex) => (
                <li key={`ol-item-${index}-${itemIndex}`}>{renderInlineMarkdownNodes(item)}</li>
              ))}
            </Box>
          );
        }

        return (
          <Typography component="p" key={`p-${index}`}>
            {renderInlineMarkdownNodes(block.text)}
          </Typography>
        );
      })}
    </Paper>
  );
};

const formatDateTimeIt = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;

  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = String(date.getFullYear());
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${day}/${month}/${year} ${hours}:${minutes}`;
};

const formatNumberIt = (value) => {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) return '-';
  return numericValue.toLocaleString('it-IT');
};

const formatCurrencyUsd = (value) => {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) return '-';
  return new Intl.NumberFormat('it-IT', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 4,
    maximumFractionDigits: 4
  }).format(numericValue);
};

const extractFilename = (headerValue, fallback) => {
  const raw = String(headerValue || '');
  const utf8Match = raw.match(/filename\*=UTF-8''([^;]+)/i);
  if (utf8Match?.[1]) {
    return decodeURIComponent(utf8Match[1]);
  }

  const simpleMatch = raw.match(/filename="?([^"]+)"?/i);
  return simpleMatch?.[1] || fallback;
};

const StatWidget = ({ title, value, subtitle, icon: Icon, color, active, onClick }) => {
  const theme = useTheme();
  const secondaryTextColor = theme.palette.mode === 'dark' ? '#cbd5e1' : theme.palette.text.secondary;

  return (
    <Paper
      elevation={0}
      variant="outlined"
      onClick={onClick}
      sx={{
        p: 2,
        height: '100%',
        cursor: onClick ? 'pointer' : 'default',
        borderColor: active ? color : 'divider',
        bgcolor: active ? alpha(color, theme.palette.mode === 'dark' ? 0.18 : 0.08) : theme.palette.background.paper,
        transition: 'all 0.18s ease'
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: '50%',
            bgcolor: alpha(color, theme.palette.mode === 'dark' ? 0.24 : 0.12),
            color,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0
          }}
        >
          <Icon size={18} />
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="body2" sx={{ fontWeight: 600, color: secondaryTextColor }}>
            {title}
          </Typography>
          <Typography variant="h5" sx={{ fontWeight: 800, lineHeight: 1.1 }}>
            {value}
          </Typography>
          {subtitle ? (
            <Typography variant="caption" sx={{ color: secondaryTextColor }}>
              {subtitle}
            </Typography>
          ) : null}
        </Box>
      </Box>
    </Paper>
  );
};

export default function AttiAiGenerazioniDashboardPage() {
  const theme = useTheme();
  const secondaryTextColor = theme.palette.mode === 'dark' ? '#cbd5e1' : theme.palette.text.secondary;
  const mutedTextColor = theme.palette.mode === 'dark' ? '#94a3b8' : theme.palette.text.secondary;
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState({
    stati: [],
    templates: [],
    richiedenti: [],
    modelli: []
  });
  const [stats, setStats] = useState({
    total_count: 0,
    in_corso_count: 0,
    completata_count: 0,
    errore_count: 0,
    token_totali_sum: 0,
    costo_usd_sum: 0
  });
  const [loading, setLoading] = useState(true);
  const [metaLoading, setMetaLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [statoFilter, setStatoFilter] = useState('all');
  const [templateFilter, setTemplateFilter] = useState('');
  const [modelFilter, setModelFilter] = useState('');
  const [requesterFilter, setRequesterFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [orderBy, setOrderBy] = useState('created_at');
  const [orderDirection, setOrderDirection] = useState('desc');
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailTab, setDetailTab] = useState('output');
  const [selectedGeneration, setSelectedGeneration] = useState(null);
  const [exporting, setExporting] = useState('');
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteSubmitting, setDeleteSubmitting] = useState(false);

  const statoOptions = useMemo(
    () => [ALL_OPTION, ...((meta.stati || []).map((item) => ({ value: item.value, label: item.label })))],
    [meta.stati]
  );
  const templateOptions = useMemo(
    () => [ALL_OPTION, ...((meta.templates || []).map((item) => ({ value: String(item.id), label: item.nome })))],
    [meta.templates]
  );
  const modelOptions = useMemo(
    () => [ALL_OPTION, ...((meta.modelli || []).map((item) => ({ value: String(item.id), label: item.label })))],
    [meta.modelli]
  );
  const requesterOptions = useMemo(
    () => [ALL_OPTION, ...((meta.richiedenti || []).map((item) => ({ value: String(item.id), label: item.name })))],
    [meta.richiedenti]
  );

  const fetchMeta = useCallback(async () => {
    try {
      setMetaLoading(true);
      const response = await api.get('/gtl-generazioni/meta');
      setMeta(response.data || {});
    } catch (fetchError) {
      console.error('Error loading gtl generazioni meta:', fetchError);
      toast.error('Errore caricamento filtri dashboard generazioni');
    } finally {
      setMetaLoading(false);
    }
  }, []);

  const fetchRows = useCallback(async () => {
    try {
      setLoading(true);
      setError('');

      const response = await api.get('/gtl-generazioni', {
        params: {
          page: page + 1,
          limit: rowsPerPage,
          orderBy,
          orderDirection,
          search: searchTerm,
          stato: statoFilter,
          templateId: templateFilter,
          modelloAiId: modelFilter,
          richiestoDa: requesterFilter,
          dateFrom,
          dateTo
        }
      });

      setRows(response.data?.data || []);
      setStats(response.data?.stats || {});
      setTotalCount(Number(response.data?.meta?.totalCount || 0));
    } catch (fetchError) {
      console.error('Error loading gtl generazioni dashboard:', fetchError);
      setRows([]);
      setTotalCount(0);
      setError('Errore nel caricamento della dashboard generazioni AI');
    } finally {
      setLoading(false);
    }
  }, [dateFrom, dateTo, modelFilter, orderBy, orderDirection, page, requesterFilter, rowsPerPage, searchTerm, statoFilter, templateFilter]);

  useEffect(() => {
    fetchMeta();
  }, [fetchMeta]);

  useEffect(() => {
    fetchRows();
  }, [fetchRows]);

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && orderDirection === 'asc';
    setOrderDirection(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
    setPage(0);
  };

  const handleOpenDetail = async (row) => {
    try {
      setDetailOpen(true);
      setDetailLoading(true);
      setDetailTab(row?.messaggio_errore ? 'error' : 'output');
      const response = await api.get(`/gtl-generazioni/${row.id}`);
      setSelectedGeneration(response.data?.data || null);
    } catch (fetchError) {
      console.error('Error loading gtl generazione detail:', fetchError);
      toast.error('Errore nel caricamento del dettaglio generazione');
      setDetailOpen(false);
      setSelectedGeneration(null);
    } finally {
      setDetailLoading(false);
    }
  };

  const handleCloseDetail = () => {
    setDetailOpen(false);
    setSelectedGeneration(null);
    setDetailTab('output');
    setExporting('');
  };

  const handleExport = async (format) => {
    if (!selectedGeneration?.id) {
      return;
    }

    try {
      setExporting(format);
      const response = await api.get(`/gtl-generazioni/${selectedGeneration.id}/export-${format}`, {
        responseType: 'blob'
      });

      const contentDisposition = response.headers?.['content-disposition'];
      const fallbackName = `generazione_${selectedGeneration.id}.${format === 'pdf' ? 'pdf' : 'doc'}`;
      const filename = extractFilename(contentDisposition, fallbackName);
      const blob = new Blob([response.data], {
        type: response.headers?.['content-type'] || 'application/octet-stream'
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Documento ${format === 'pdf' ? 'PDF' : 'Word'} scaricato`);
    } catch (exportError) {
      console.error(`Error exporting gtl generazione ${format}:`, exportError);
      toast.error(`Errore export ${format === 'pdf' ? 'PDF' : 'Word'}`);
    } finally {
      setExporting('');
    }
  };

  const handleOpenDeleteDialog = (row) => {
    if (!row?.id) {
      return;
    }

    setDeleteTarget(row);
  };

  const handleCloseDeleteDialog = () => {
    if (deleteSubmitting) {
      return;
    }

    setDeleteTarget(null);
  };

  const handleDeleteGeneration = async () => {
    if (!deleteTarget?.id) {
      return;
    }

    try {
      setDeleteSubmitting(true);
      await api.delete(`/gtl-generazioni/${deleteTarget.id}`);
      toast.success('Generazione AI cancellata');
      setDeleteTarget(null);

      if (selectedGeneration?.id === deleteTarget.id) {
        handleCloseDetail();
      }

      await fetchRows();
    } catch (deleteError) {
      console.error('Error deleting gtl generazione:', deleteError);
      toast.error(deleteError?.response?.data?.message || 'Errore durante la cancellazione della generazione');
    } finally {
      setDeleteSubmitting(false);
    }
  };

  const detailStatusChip = useMemo(() => {
    const config = STATUS_CHIPS[selectedGeneration?.stato] || STATUS_CHIPS.in_corso;
    return (
      <Chip
        size="small"
        color={config.color}
        variant={config.variant}
        label={config.label}
        sx={{ fontWeight: 700 }}
      />
    );
  }, [selectedGeneration?.stato]);

  const detailMetaChips = useMemo(() => {
    if (!selectedGeneration) {
      return [];
    }

    return [
      `Input ${formatNumberIt(selectedGeneration.token_input)} tok`,
      `Output ${formatNumberIt(selectedGeneration.token_output)} tok`,
      `Totali ${formatNumberIt(selectedGeneration.token_totali)} tok`,
      `Costo ${formatCurrencyUsd(selectedGeneration.costo_usd)}`
    ];
  }, [selectedGeneration]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, alignItems: 'flex-start', flexWrap: 'wrap' }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 800 }}>
            Dashboard generazioni
          </Typography>
          <Typography variant="body2" sx={{ color: secondaryTextColor }}>
            Monitoraggio operativo delle bozze prodotte con AI, con dettaglio audit ed export documento.
          </Typography>
        </Box>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon size={16} />}
          onClick={fetchRows}
          disabled={loading}
        >
          Aggiorna
        </Button>
      </Box>

      <Grid container spacing={2}>
        <Grid item xs={12} md={3}>
          <StatWidget
            title="Totale generazioni"
            value={formatNumberIt(stats.total_count)}
            subtitle={`${formatCurrencyUsd(stats.costo_usd_sum)} spesa`}
            icon={SparklesIcon}
            color="#6366f1"
            active={statoFilter === 'all'}
            onClick={() => {
              setStatoFilter('all');
              setPage(0);
            }}
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <StatWidget
            title="Completate"
            value={formatNumberIt(stats.completata_count)}
            subtitle={`${formatNumberIt(stats.token_totali_sum)} token`}
            icon={CheckCircle2Icon}
            color="#16a34a"
            active={statoFilter === 'completata'}
            onClick={() => {
              setStatoFilter('completata');
              setPage(0);
            }}
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <StatWidget
            title="In corso"
            value={formatNumberIt(stats.in_corso_count)}
            subtitle="Richieste ancora aperte"
            icon={BotIcon}
            color="#0284c7"
            active={statoFilter === 'in_corso'}
            onClick={() => {
              setStatoFilter('in_corso');
              setPage(0);
            }}
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <StatWidget
            title="Errori"
            value={formatNumberIt(stats.errore_count)}
            subtitle="Da verificare"
            icon={AlertTriangleIcon}
            color="#dc2626"
            active={statoFilter === 'errore'}
            onClick={() => {
              setStatoFilter('errore');
              setPage(0);
            }}
          />
        </Grid>
      </Grid>

      <StyledCard>
        {(loading || metaLoading) && <LinearProgress />}
        <CardContent sx={{ p: 3 }}>
          <Grid container spacing={1.5}>
            <Grid item xs={12} md={2.5}>
              <TextField
                fullWidth
                size="small"
                value={searchTerm}
                onChange={(event) => {
                  setSearchTerm(event.target.value);
                  setPage(0);
                }}
                placeholder="Cerca pratica, template, modello, errore..."
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon size={16} />
                    </InputAdornment>
                  )
                }}
              />
            </Grid>
            <Grid item xs={12} md={1.5}>
              <Autocomplete
                fullWidth
                size="small"
                options={statoOptions}
                value={statoOptions.find((item) => item.value === (statoFilter === 'all' ? '' : statoFilter)) || ALL_OPTION}
                onChange={(_event, option) => {
                  setStatoFilter(option?.value || 'all');
                  setPage(0);
                }}
                isOptionEqualToValue={(option, value) => option.value === value.value}
                getOptionLabel={(option) => option?.label || ''}
                renderInput={(params) => <TextField {...params} label="Stato" />}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <Autocomplete
                fullWidth
                size="small"
                options={templateOptions}
                value={templateOptions.find((item) => item.value === String(templateFilter || '')) || ALL_OPTION}
                onChange={(_event, option) => {
                  setTemplateFilter(option?.value || '');
                  setPage(0);
                }}
                isOptionEqualToValue={(option, value) => option.value === value.value}
                getOptionLabel={(option) => option?.label || ''}
                renderInput={(params) => <TextField {...params} label="Template" />}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <Autocomplete
                fullWidth
                size="small"
                options={modelOptions}
                value={modelOptions.find((item) => item.value === String(modelFilter || '')) || ALL_OPTION}
                onChange={(_event, option) => {
                  setModelFilter(option?.value || '');
                  setPage(0);
                }}
                isOptionEqualToValue={(option, value) => option.value === value.value}
                getOptionLabel={(option) => option?.label || ''}
                renderInput={(params) => <TextField {...params} label="Modello AI" />}
              />
            </Grid>
            <Grid item xs={12} md={1.5}>
              <Autocomplete
                fullWidth
                size="small"
                options={requesterOptions}
                value={requesterOptions.find((item) => item.value === String(requesterFilter || '')) || ALL_OPTION}
                onChange={(_event, option) => {
                  setRequesterFilter(option?.value || '');
                  setPage(0);
                }}
                isOptionEqualToValue={(option, value) => option.value === value.value}
                getOptionLabel={(option) => option?.label || ''}
                renderInput={(params) => <TextField {...params} label="Richiedente" />}
              />
            </Grid>
            <Grid item xs={12} md={1.25}>
              <TextField
                fullWidth
                size="small"
                label="Dal"
                type="date"
                value={dateFrom}
                onChange={(event) => {
                  setDateFrom(event.target.value);
                  setPage(0);
                }}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={1.25}>
              <TextField
                fullWidth
                size="small"
                label="Al"
                type="date"
                value={dateTo}
                onChange={(event) => {
                  setDateTo(event.target.value);
                  setPage(0);
                }}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
          </Grid>

          <Box sx={{ display: 'flex', gap: 1, mt: 1.5, flexWrap: 'wrap' }}>
            <Button
              size="small"
              variant="text"
              onClick={() => {
                setSearchTerm('');
                setStatoFilter('all');
                setTemplateFilter('');
                setModelFilter('');
                setRequesterFilter('');
                setDateFrom('');
                setDateTo('');
                setPage(0);
              }}
            >
              Azzera filtri
            </Button>
            <Chip size="small" variant="outlined" label={`Righe ${formatNumberIt(totalCount)}`} />
            <Chip size="small" variant="outlined" label={`Token ${formatNumberIt(stats.token_totali_sum)}`} />
            <Chip size="small" variant="outlined" label={`Costo ${formatCurrencyUsd(stats.costo_usd_sum)}`} />
          </Box>

          {error ? (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          ) : null}

          <TableContainer
            component={Paper}
            variant="outlined"
            sx={{
              mt: 2,
              bgcolor: theme.palette.mode === 'dark' ? alpha(theme.palette.common.white, 0.04) : theme.palette.background.paper
            }}
          >
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: theme.palette.mode === 'dark' ? alpha(theme.palette.common.white, 0.03) : 'transparent' }}>
                  {[
                    { id: 'created_at', label: 'Creata il' },
                    { id: 'pratica_titolo', label: 'Pratica / Template' },
                    { id: 'stato', label: 'Stato' },
                    { id: 'modello_ai_snapshot', label: 'Modello AI' },
                    { id: 'token_totali', label: 'Token' },
                    { id: 'costo_usd', label: 'Costo' },
                    { id: 'richiesto_da_nome', label: 'Richiedente' },
                    { id: 'completata_at', label: 'Completata il' }
                  ].map((column) => (
                    <TableCell key={column.id} sortDirection={orderBy === column.id ? orderDirection : false}>
                      {SORTABLE_COLUMNS[column.id] ? (
                        <TableSortLabel
                          active={orderBy === column.id}
                          direction={orderBy === column.id ? orderDirection : 'asc'}
                          onClick={() => handleRequestSort(SORTABLE_COLUMNS[column.id])}
                        >
                          {column.label}
                        </TableSortLabel>
                      ) : (
                        column.label
                      )}
                    </TableCell>
                  ))}
                  <TableCell align="right" sx={{ width: 92, minWidth: 92, whiteSpace: 'nowrap' }}>
                    Azioni
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {!loading && rows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} align="center" sx={{ py: 5 }}>
                      <Typography variant="body2" sx={{ color: secondaryTextColor }}>
                        Nessuna generazione trovata con i filtri correnti.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : null}

                {rows.map((row) => {
                  const chipConfig = STATUS_CHIPS[row.stato] || STATUS_CHIPS.in_corso;
                  return (
                    <TableRow key={row.id} hover>
                      <TableCell>{formatDateTimeIt(row.created_at)}</TableCell>
                      <TableCell sx={{ minWidth: 260 }}>
                        <Typography variant="body2" sx={{ fontWeight: 700 }}>
                          {row.pratica_titolo || '-'}
                        </Typography>
                        <Typography variant="caption" sx={{ color: mutedTextColor }} display="block">
                          {row.template_nome || '-'}
                        </Typography>
                        <Typography variant="caption" sx={{ color: mutedTextColor }} display="block">
                          {row.cliente_nome || 'Cliente non disponibile'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip size="small" color={chipConfig.color} variant={chipConfig.variant} label={chipConfig.label} />
                      </TableCell>
                      <TableCell sx={{ maxWidth: 240 }}>
                        <Typography variant="body2">{row.modello_ai_snapshot || '-'}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 700 }}>
                          {formatNumberIt(row.token_totali)}
                        </Typography>
                        <Typography variant="caption" sx={{ color: mutedTextColor }} display="block">
                          In {formatNumberIt(row.token_input)} / Out {formatNumberIt(row.token_output)}
                        </Typography>
                      </TableCell>
                      <TableCell>{formatCurrencyUsd(row.costo_usd)}</TableCell>
                      <TableCell>{row.richiesto_da_nome || '-'}</TableCell>
                      <TableCell>{formatDateTimeIt(row.completata_at)}</TableCell>
                      <TableCell align="right" sx={{ width: 92, minWidth: 92, whiteSpace: 'nowrap' }}>
                        <Box sx={{ display: 'inline-flex', alignItems: 'center', flexWrap: 'nowrap', gap: 0.25 }}>
                          <Tooltip title="Apri dettaglio">
                            <IconButton size="small" onClick={() => handleOpenDetail(row)}>
                              <EyeIcon size={16} />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title={row.stato === 'in_corso' ? 'Non puoi cancellare una generazione in corso' : 'Cancella generazione'}>
                            <span>
                              <IconButton
                                size="small"
                                color="error"
                                disabled={row.stato === 'in_corso'}
                                onClick={() => handleOpenDeleteDialog(row)}
                              >
                                <Trash2Icon size={16} />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            component="div"
            count={totalCount}
            page={page}
            onPageChange={(_event, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(event) => {
              setRowsPerPage(parseInt(event.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[10, 25, 50]}
          />
        </CardContent>
      </StyledCard>

      <Dialog open={detailOpen} onClose={handleCloseDetail} fullWidth maxWidth="lg">
        <DialogTitle sx={{ pb: 1.5 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, alignItems: 'flex-start', flexWrap: 'wrap' }}>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 800 }}>
                {selectedGeneration ? `Generazione #${selectedGeneration.id}` : 'Dettaglio generazione'}
              </Typography>
              <Typography variant="body2" sx={{ color: secondaryTextColor }}>
                {selectedGeneration?.pratica_titolo || 'Caricamento dettaglio...'}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
              {selectedGeneration ? detailStatusChip : null}
              <Button
                size="small"
                variant="outlined"
                startIcon={<DownloadIcon size={16} />}
                onClick={() => handleExport('pdf')}
                disabled={!selectedGeneration || exporting === 'pdf'}
              >
                PDF
              </Button>
              <Button
                size="small"
                variant="outlined"
                startIcon={<DownloadIcon size={16} />}
                onClick={() => handleExport('word')}
                disabled={!selectedGeneration || exporting === 'word'}
              >
                Word (.doc)
              </Button>
              <Button
                size="small"
                color="error"
                variant="outlined"
                startIcon={<Trash2Icon size={16} />}
                onClick={() => handleOpenDeleteDialog(selectedGeneration)}
                disabled={!selectedGeneration || selectedGeneration.stato === 'in_corso' || deleteSubmitting}
              >
                Elimina
              </Button>
            </Box>
          </Box>
        </DialogTitle>

        <DialogContent dividers sx={{ p: 0 }}>
          {detailLoading ? <LinearProgress /> : null}

          {selectedGeneration ? (
            <Box sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
                {detailMetaChips.map((label) => (
                  <Chip key={label} size="small" variant="outlined" label={label} />
                ))}
                <Chip size="small" variant="outlined" label={`Creata ${formatDateTimeIt(selectedGeneration.created_at)}`} />
                <Chip size="small" variant="outlined" label={`Completata ${formatDateTimeIt(selectedGeneration.completata_at)}`} />
              </Box>

              <Tabs value={detailTab} onChange={(_event, value) => setDetailTab(value)} sx={{ mb: 2 }}>
                <Tab value="output" label="Bozza generata" />
                <Tab value="prompt" label="Prompt finale" />
                <Tab value="meta" label="Metadati" />
                <Tab value="error" label="Errore" disabled={!selectedGeneration.messaggio_errore} />
              </Tabs>

              {detailTab === 'output' ? (
                <DraftContentPreview value={selectedGeneration.output} outputFormat={selectedGeneration.output_format} />
              ) : null}

              {detailTab === 'prompt' ? (
                <Paper
                  variant="outlined"
                  sx={{
                    p: 2.25,
                    minHeight: 320,
                    whiteSpace: 'pre-wrap',
                    fontFamily: 'Consolas, Monaco, monospace',
                    fontSize: 13,
                    lineHeight: 1.6,
                    bgcolor: theme.palette.mode === 'dark' ? alpha(theme.palette.common.white, 0.04) : '#0f172a',
                    color: theme.palette.mode === 'dark' ? theme.palette.text.primary : '#e2e8f0',
                    borderColor: theme.palette.divider
                  }}
                >
                  {selectedGeneration.prompt_finale || 'Prompt finale non disponibile.'}
                </Paper>
              ) : null}

              {detailTab === 'meta' ? (
                <Grid container spacing={2}>
                  {[
                    ['Template', selectedGeneration.template_nome],
                    ['Cliente', selectedGeneration.cliente_nome],
                    ['Modello AI', selectedGeneration.modello_ai_snapshot],
                    ['Richiedente', selectedGeneration.richiesto_da_nome],
                    ['Stato', selectedGeneration.stato],
                    ['Token input', formatNumberIt(selectedGeneration.token_input)],
                    ['Token output', formatNumberIt(selectedGeneration.token_output)],
                    ['Token totali', formatNumberIt(selectedGeneration.token_totali)],
                    ['Costo USD', formatCurrencyUsd(selectedGeneration.costo_usd)],
                    ['Codice errore', selectedGeneration.codice_errore || '-']
                  ].map(([label, value]) => (
                    <Grid item xs={12} md={6} key={label}>
                      <Paper variant="outlined" sx={{ p: 2 }}>
                        <Typography variant="caption" sx={{ color: mutedTextColor }} display="block">
                          {label}
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 700 }}>
                          {value || '-'}
                        </Typography>
                      </Paper>
                    </Grid>
                  ))}
                </Grid>
              ) : null}

              {detailTab === 'error' ? (
                <Alert severity="error" sx={{ whiteSpace: 'pre-wrap' }}>
                  {selectedGeneration.messaggio_errore || 'Nessun errore registrato.'}
                </Alert>
              ) : null}
            </Box>
          ) : (
            !detailLoading && (
              <Box sx={{ p: 3 }}>
                <Alert severity="info">Dettaglio generazione non disponibile.</Alert>
              </Box>
            )
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={handleCloseDetail}>Chiudi</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(deleteTarget)} onClose={handleCloseDeleteDialog} fullWidth maxWidth="sm">
        <DialogTitle>Cancella generazione</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" sx={{ mb: 1.5 }}>
            Stai per rimuovere dalla dashboard la generazione selezionata.
          </Typography>
          <Paper variant="outlined" sx={{ p: 2, mb: 1.5 }}>
            <Typography variant="body2" sx={{ fontWeight: 700 }}>
              {deleteTarget ? `Generazione #${deleteTarget.id}` : '-'}
            </Typography>
            <Typography variant="body2" sx={{ color: secondaryTextColor }}>
              {deleteTarget?.pratica_titolo || deleteTarget?.template_nome || 'Generazione senza titolo'}
            </Typography>
            <Typography variant="caption" display="block" sx={{ mt: 0.75, color: mutedTextColor }}>
              Stato: {STATUS_CHIPS[deleteTarget?.stato]?.label || '-'}
            </Typography>
          </Paper>
          <Alert severity="warning">
            La cancellazione è di tipo logico: la generazione viene esclusa dalle liste operative ma resta tracciata a livello audit.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog} disabled={deleteSubmitting}>
            Annulla
          </Button>
          <Button color="error" variant="contained" onClick={handleDeleteGeneration} disabled={deleteSubmitting}>
            {deleteSubmitting ? 'Cancellazione...' : 'Conferma cancellazione'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
